import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pag, setPag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const [allBooks, setAllBooks] = useState([]);
  const [editingUser, setEditingUser] = useState(null); // The user whose access we are managing
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [bookSearch, setBookSearch] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await adminAPI.getUsers(params);
      setUsers(res.data || []);
      setPag(res.pagination);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadBooks = async () => {
    try {
      const res = await adminAPI.getBooks({ limit: 5000 });
      setAllBooks(res.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [page, search]);
  useEffect(() => { loadBooks(); }, []);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  return (
    <div style={{ padding: 32, fontFamily: 'Nunito,sans-serif' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 26, fontWeight: 700, color: '#2C2C35' }}>
          User <em style={{ color: '#C97B77', fontStyle: 'italic' }}>Management</em>
        </div>
        <div style={{ fontSize: 13, color: '#888899', marginTop: 4 }}>
          {pag?.total || 0} registered users
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', maxWidth: 400, marginBottom: 20 }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#AAAABC' }}>🔍</span>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name or email…"
          style={{ width: '100%', padding: '10px 14px 10px 38px', borderRadius: 10, border: '2px solid #E8E4DF', fontSize: 13, fontFamily: 'Nunito,sans-serif', outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #E8E4DF', overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 180px 100px 140px 80px 80px 80px', padding: '10px 20px',
          background: '#F5F2EE', borderBottom: '1px solid #E8E4DF', fontSize: 10, fontWeight: 700, color: '#888899', textTransform: 'uppercase', letterSpacing: 1.5
        }}>
          <div>User</div><div>Email</div><div>Plan</div><div>Extra Books</div><div>Bookmarks</div><div>Notes</div><div>Joined</div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40, gap: 10 }}>
            <div style={{ width: 22, height: 22, border: '2px solid #E8E4DF', borderTopColor: '#C97B77', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
            <span style={{ fontSize: 13, color: '#888899' }}>Loading users…</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888899', fontSize: 13 }}>No users found</div>
        ) : users.map((u, i) => {
          const displayName = u.name || u.email?.split('@')[0] || '—';
          const initials = (u.name || u.email || 'U').split(/[\s@]/).map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'U';

          return (
            <div key={String(u._id)} style={{
              display: 'grid', gridTemplateColumns: '1fr 180px 120px 140px 80px 80px 80px',
              padding: '12px 20px', borderBottom: '1px solid #F0ECE8', alignItems: 'center',
              background: i % 2 === 0 ? 'white' : '#FAFAF8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', background: '#F9EDEC', border: '1px solid #E8B4B1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#C97B77', flexShrink: 0
                }}>
                  {initials}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C35' }}>{displayName}</div>
                  <div style={{ fontSize: 10, color: '#888899' }}>{u.email || u.auth0Id?.substring(0, 25) || '—'}</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#555567', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email}>{u.email || '—'}</div>
              <div>
                <span
                  style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 8,
                    background: u.plan?.includes('PRO') ? '#F0EDF8' : '#EBF4F0',
                    color: u.plan?.includes('PRO') ? '#9B8EC4' : '#7A9E8E',
                    fontFamily: 'Nunito,sans-serif'
                  }}
                >
                  {u.plan || 'Free'}
                </span>
              </div>
              <div>
                <button
                  onClick={() => {
                    setEditingUser(u);
                    setSelectedBooks(u.access_books || []);
                  }}
                  style={{
                    fontSize: 10, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: '1px solid #C97B77',
                    background: 'white', color: '#C97B77', cursor: 'pointer', fontFamily: 'Nunito,sans-serif'
                  }}
                  onMouseEnter={e => { e.target.style.background = '#F9EDEC'; }}
                  onMouseLeave={e => { e.target.style.background = 'white'; }}
                >
                  Manage Books ({u.access_books?.length || 0})
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#555567', textAlign: 'center' }}>{u.bookmarks?.length || 0}</div>
              <div style={{ fontSize: 12, color: '#555567', textAlign: 'center' }}>{u.annotationCount || 0}</div>
              <div style={{ fontSize: 11, color: '#888899' }}>{formatDate(u.createdAt)}</div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pag && pag.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, alignItems: 'center' }}>
          <button disabled={!pag.has_prev} onClick={() => setPage(p => p - 1)}
            style={{ padding: '8px 16px', borderRadius: 8, border: '2px solid #E8E4DF', background: 'white', color: '#555567', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: pag.has_prev ? 1 : .4 }}>‹ Prev</button>
          <span style={{ fontSize: 12, color: '#888899' }}>Page {pag.page} of {pag.total_pages}</span>
          <button disabled={!pag.has_next} onClick={() => setPage(p => p + 1)}
            style={{ padding: '8px 16px', borderRadius: 8, border: '2px solid #E8E4DF', background: 'white', color: '#555567', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: pag.has_next ? 1 : .4 }}>Next ›</button>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          background: '#2C2C35', color: 'white', padding: '12px 24px', borderRadius: 12, fontSize: 13, fontWeight: 700,
          boxShadow: '0 4px 20px rgba(0,0,0,.2)', fontFamily: 'Nunito,sans-serif'
        }}>
          {toast}
        </div>
      )}

      {/* Book Access Modal */}
      {editingUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', width: 600, maxHeight: '80vh', borderRadius: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: 24, borderBottom: '1px solid #E8E4DF', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#2C2C35' }}>Manage Access</div>
                <div style={{ fontSize: 12, color: '#888899' }}>for {editingUser.name || editingUser.email}</div>
              </div>
              <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888899' }}>✕</button>
            </div>

            <div style={{ padding: 16, background: '#F5F2EE', borderBottom: '1px solid #E8E4DF', display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                value={bookSearch}
                onChange={e => setBookSearch(e.target.value)}
                placeholder="Search books..."
                style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid #DED4C0', outline: 'none', fontSize: 13 }}
              />
              <button
                onClick={() => setSelectedBooks(allBooks.map(b => b._id))}
                style={{ fontSize: 10, fontWeight: 700, color: '#C97B77', background: 'none', border: '1px solid #C97B77', padding: '8px 12px', borderRadius: 8, cursor: 'pointer' }}>
                Select All
              </button>

            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {allBooks
                .filter(b => !bookSearch || decrypt(b.book_name || b.book_title)?.toLowerCase().includes(bookSearch.toLowerCase()))
                .map(b => (
                  <label key={String(b._id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #F0ECE8', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedBooks.includes(b._id)}
                      onChange={e => {
                        const id = b._id;
                        if (e.target.checked) setSelectedBooks(prev => [...prev, id]);
                        else setSelectedBooks(prev => prev.filter(x => x !== id));
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <div style={{ fontSize: 13, color: '#2C2C35' }}>{decrypt(b.book_name || b.book_title)}</div>
                  </label>
                ))}
            </div>

            <div style={{ padding: 20, borderTop: '1px solid #E8E4DF', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setEditingUser(null)} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #E8E4DF', background: 'white', color: '#888899', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={async () => {
                  try {
                    const uid = editingUser.auth0Id || editingUser.userId || String(editingUser._id);
                    await adminAPI.updateUserAccessBooks(uid, selectedBooks);
                    setUsers(prev => prev.map(u => (u.auth0Id || u.userId || String(u._id)) === uid ? { ...u, access_books: selectedBooks } : u));
                    showToast('Access books updated successfully ✓');
                    setEditingUser(null);
                  } catch (e) { showToast(e.message, 'error'); }
                }}
                style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: '#C97B77', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Save Access
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}